/* eslint-env es6 */
/**
 * The `RegionACLAction` members that describe an action with no instrument and
 * no venue behind it.
 *
 * The enum still carries the instrument actions — STOCK_BUY, BOND_SELL,
 * VENTURE, MUTUAL_FUND and the rest — and config already storing them keeps
 * enforcing exactly as before. They are not offered for new config: none of
 * them can name an exchange, so with a second venue live they can only say
 * "block this everywhere", and they speak the pre-v2 vocabulary the
 * instruments themselves no longer use. Those restrictions belong in `rules`.
 *
 * `ALL` stays, and still blocks everything including instruments.
 */
/**
 * The client products a restriction can be scoped to.
 *
 * EMPTY MEANS ALL PRODUCTS — the same convention as UserRole.products, and the
 * back-compat hinge: every entry written before this field existed has none and
 * must keep applying to both apps. Leaving the picker empty is therefore the
 * correct way to say "restrict everywhere", not a missing value.
 */
export const PRODUCT_OPTIONS = [
  {value: 'WEB', label: 'Web'},
  {value: 'MOBILE', label: 'Mobile'},
];

export const ACL_OPTIONS = [
  {value: 'ALL', label: 'All'},
  {value: 'DEPOSIT', label: 'Deposit'},
  {value: 'WITHDRAWAL', label: 'Withdrawal'},
  {value: 'KYC', label: 'KYC'},
  {value: 'DABA_PRO', label: 'Daba Pro'},
  {value: 'DABA_ACADEMY', label: 'Daba Academy'},
  {value: 'SAVINGS', label: 'Savings'},
  {value: 'CIRCLE', label: 'Circle'},
];

/**
 * `AssetClassKey` members offered on a rule.
 *
 * The enum also holds `STOCK`, the pre-v2 name for what is now EQUITIES. It is
 * left out: an admin picking it would write a restriction in a vocabulary no
 * current instrument reports.
 */
export const ASSET_CLASS_KEY_OPTIONS = [
  {value: 'EQUITIES', label: 'Equities'},
  {value: 'FIXED_INCOMES', label: 'Fixed income'},
  {value: 'FUNDS', label: 'Funds'},
  {value: 'CASH', label: 'Cash'},
  {value: 'COMMODITIES', label: 'Commodities'},
  {value: 'REAL_ESTATE', label: 'Real estate'},
  {value: 'ALTERNATIVES', label: 'Alternatives'},
  {value: 'CRYPTOCURRENCY', label: 'Cryptocurrency'},
];

/**
 * `InstrumentKey` members offered on a rule, grouped by the class they sit
 * under purely so the list is readable.
 *
 * The grouping is presentational: a rule's two key lists are OR'd, never
 * nested, so picking a class here would not narrow anything (see the helper
 * text on the rule builder).
 *
 * `STOCK` and `VENTURE` are omitted for the same reason as in the class list —
 * they are retired names for PUBLIC_EQUITY and PRIVATE_EQUITY.
 */
export const INSTRUMENT_KEY_GROUPS = [
  {
    label: 'Equities',
    options: [
      {value: 'PUBLIC_EQUITY', label: 'Public equity (listed stock)'},
      {value: 'PRIVATE_EQUITY', label: 'Private equity'},
      {value: 'IPO', label: 'IPO'},
    ],
  },
  {
    label: 'Fixed income',
    options: [
      {value: 'BOND', label: 'Bond'},
      {value: 'COMMERCIAL_PAPER', label: 'Commercial paper'},
    ],
  },
  {
    label: 'Funds',
    options: [
      {value: 'MUTUAL_FUND', label: 'Mutual fund'},
      {value: 'ETF', label: 'ETF'},
      {value: 'INDEX_FUND', label: 'Index fund'},
      {value: 'HEDGE_FUND', label: 'Hedge fund'},
      {value: 'VENTURE_FUND', label: 'Venture fund'},
      {value: 'VENTURE_CAPITAL_FUND', label: 'Venture capital fund'},
      {value: 'PRIVATE_EQUITY_FUND', label: 'Private equity fund'},
      {value: 'MONEY_MARKET_FUND', label: 'Money market fund'},
    ],
  },
  {
    label: 'Cash',
    options: [
      {value: 'CERTIFICATE_OF_DEPOSIT', label: 'Certificate of deposit'},
      {value: 'SAVINGS_ACCOUNT', label: 'Savings account'},
    ],
  },
  {
    label: 'Commodities',
    options: [
      {value: 'PRECIOUS_METAL', label: 'Precious metal'},
      {value: 'AGRICULTURAL', label: 'Agricultural'},
      {value: 'LIVESTOCK', label: 'Livestock'},
    ],
  },
  {
    label: 'Real estate',
    options: [
      {value: 'DIRECT_REAL_ESTATE', label: 'Direct real estate'},
      {value: 'REIT', label: 'REIT'},
    ],
  },
  {
    label: 'Other',
    options: [{value: 'COLLECTION', label: 'Collection'}],
  },
];

export const OPERATION_OPTIONS = [
  {value: 'ALL', label: 'Buy & sell'},
  {value: 'BUY', label: 'Buy only'},
  {value: 'SELL', label: 'Sell only'},
];

export const getDefaultRule = () => ({
  assetClassKeys: [],
  instrumentKeys: [],
  operation: 'ALL',
  exchanges: [],
});

/**
 * Which entries are the *first* to name each of their countries.
 *
 * A country may appear in several entries now, and the gate treats the two
 * halves of an entry differently: every matching entry's `rules` are unioned,
 * but `acl`, `cutoffDate` and `message` are read from the first matching entry
 * only. So a later entry for a country it has already seen can carry rules,
 * and nothing else it carries will ever be read.
 *
 * Returns one descriptor per entry, in order:
 *   `shadowedCountries` countries an earlier entry already claimed
 *   `isSecondary`       every one of its countries is shadowed, so its acl,
 *                       cutoff date and message are dead config
 */
export const entryPrecedence = (entries = []) => {
  const claimed = new Set();

  return entries.map(entry => {
    const countries = entry?.region || [];
    const shadowedCountries = countries.filter(country => claimed.has(country));
    countries.forEach(country => claimed.add(country));

    return {
      shadowedCountries,
      isSecondary:
        countries.length > 0 && shadowedCountries.length === countries.length,
    };
  });
};

/** The instrument keys each asset class stands for, for overlap checks. */
const ASSET_CLASS_MEMBERS = {
  EQUITIES: ['PUBLIC_EQUITY', 'PRIVATE_EQUITY', 'IPO'],
  FIXED_INCOMES: ['BOND', 'COMMERCIAL_PAPER'],
  FUNDS: [
    'MUTUAL_FUND',
    'ETF',
    'INDEX_FUND',
    'HEDGE_FUND',
    'VENTURE_FUND',
    'VENTURE_CAPITAL_FUND',
    'PRIVATE_EQUITY_FUND',
    'MONEY_MARKET_FUND',
  ],
  CASH: ['CERTIFICATE_OF_DEPOSIT', 'SAVINGS_ACCOUNT'],
  COMMODITIES: ['PRECIOUS_METAL', 'AGRICULTURAL', 'LIVESTOCK'],
  REAL_ESTATE: ['DIRECT_REAL_ESTATE', 'REIT'],
};

/**
 * What each instrument action in `acl` restricts, in rule vocabulary.
 *
 * These are the actions the UI no longer offers. They still enforce, and the
 * migration has copied them into `rules` alongside — the same restriction
 * written twice, which is expected rather than a conflict. The map exists to
 * answer one question: does an action already block, on every venue, what a
 * rule is trying to block on one?
 *
 * `instrumentKeys: null` means the action is not scoped to an instrument at
 * all, so it covers everything.
 */
const INSTRUMENT_ACL_SCOPE = {
  BUY: {instrumentKeys: null, operation: 'BUY'},
  SELL: {instrumentKeys: null, operation: 'SELL'},
  STOCK: {instrumentKeys: ['PUBLIC_EQUITY'], operation: 'ALL'},
  STOCK_BUY: {instrumentKeys: ['PUBLIC_EQUITY'], operation: 'BUY'},
  STOCK_SELL: {instrumentKeys: ['PUBLIC_EQUITY'], operation: 'SELL'},
  BOND: {instrumentKeys: ['BOND'], operation: 'ALL'},
  BOND_BUY: {instrumentKeys: ['BOND'], operation: 'BUY'},
  BOND_SELL: {instrumentKeys: ['BOND'], operation: 'SELL'},
  MUTUAL_FUND: {instrumentKeys: ['MUTUAL_FUND'], operation: 'ALL'},
  MUTUAL_FUND_BUY: {instrumentKeys: ['MUTUAL_FUND'], operation: 'BUY'},
  MUTUAL_FUND_SELL: {instrumentKeys: ['MUTUAL_FUND'], operation: 'SELL'},
  VENTURE: {instrumentKeys: ['PRIVATE_EQUITY'], operation: 'ALL'},
  VENTURE_BUY: {instrumentKeys: ['PRIVATE_EQUITY'], operation: 'BUY'},
  VENTURE_FUND: {instrumentKeys: ['VENTURE_FUND'], operation: 'ALL'},
  VENTURE_FUND_BUY: {instrumentKeys: ['VENTURE_FUND'], operation: 'BUY'},
  IPO_BUY: {instrumentKeys: ['IPO'], operation: 'BUY'},
  COLLECTIONS: {instrumentKeys: ['COLLECTION'], operation: 'ALL'},
  COLLECTION_BUY: {instrumentKeys: ['COLLECTION'], operation: 'BUY'},
};

const INSTRUMENT_ACL_LABELS = {
  BUY: 'Buy (all instruments)',
  SELL: 'Sell (all instruments)',
  STOCK: 'Stock',
  STOCK_BUY: 'Stock — buy',
  STOCK_SELL: 'Stock — sell',
  BOND: 'Bond',
  BOND_BUY: 'Bond — buy',
  BOND_SELL: 'Bond — sell',
  MUTUAL_FUND: 'Mutual fund',
  MUTUAL_FUND_BUY: 'Mutual fund — buy',
  MUTUAL_FUND_SELL: 'Mutual fund — sell',
  VENTURE: 'Venture',
  VENTURE_BUY: 'Venture — buy',
  VENTURE_FUND: 'Venture fund',
  VENTURE_FUND_BUY: 'Venture fund — buy',
  IPO_BUY: 'IPO — buy',
  COLLECTIONS: 'Collections',
  COLLECTION_BUY: 'Collection — buy',
};

export const aclActionLabel = action =>
  ACL_OPTIONS.find(option => option.value === action)?.label ??
  INSTRUMENT_ACL_LABELS[action] ??
  action;

/**
 * The action list for one entry: the eight offered actions, plus any
 * instrument action the entry already stores.
 *
 * Stored ones are included so they render as their name rather than a raw
 * enum, and so they stay removable — narrowing a restriction to one venue
 * means taking the broad action off this list. They are marked, and appear
 * only for an entry that already has them, so no new config can pick one up.
 */
export const aclOptionsFor = (value = []) => {
  const stored = (Array.isArray(value) ? value : [])
    .filter(action => INSTRUMENT_ACL_SCOPE[action])
    .map(action => ({
      value: action,
      label: `${aclActionLabel(action)} (existing)`,
    }));

  return [...ACL_OPTIONS, ...stored];
};

const keysForRule = rule => {
  const fromClasses = (rule?.assetClassKeys || []).reduce(
    (all, assetClass) => all.concat(ASSET_CLASS_MEMBERS[assetClass] || []),
    [],
  );
  const keys = [...(rule?.instrumentKeys || []), ...fromClasses];
  // Neither list filled is a wildcard: the rule covers every instrument.
  return keys.length ? keys : null;
};

const directionsOverlap = (a, b) => a === 'ALL' || b === 'ALL' || a === b;

const scopesOverlap = (ruleKeys, actionKeys) =>
  !ruleKeys || !actionKeys || ruleKeys.some(key => actionKeys.includes(key));

/**
 * Actions on the entry that make a venue-scoped rule pointless.
 *
 * A rule naming an exchange only narrows anything if nothing broader is still
 * blocking the same instruments everywhere. While `STOCK_BUY` sits in `acl`,
 * the entry-point check blocks stock buying before any instrument is loaded,
 * so a BRVM-scoped rule beside it leaves NGX blocked too and reads as the
 * feature being broken.
 *
 * Only reported for a rule that actually names a venue — an unscoped rule and
 * a broad action say the same thing, which is exactly what the migration
 * produced and is not worth flagging.
 */
export const shadowingAclActions = (rule, acl = []) => {
  if (!rule?.exchanges?.length) {
    return [];
  }

  const ruleKeys = keysForRule(rule);
  const ruleOperation = rule?.operation || 'ALL';

  return (Array.isArray(acl) ? acl : []).filter(action => {
    const scope = INSTRUMENT_ACL_SCOPE[action];
    if (!scope) {
      return false;
    }
    return (
      directionsOverlap(ruleOperation, scope.operation) &&
      scopesOverlap(ruleKeys, scope.instrumentKeys)
    );
  });
};
